import axios from "axios";
import React, { useState, useEffect, useRef } from "react";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import EntrepreneurTermsNConditions from "./EntrepreneurTermsNConditions";
import Footer from "./Footer";
import TopBar from "./Topbar";
import useConfig from "../hooks/useConfig";
import { loadPublicConfigs } from "../redux/slices/configSlice";
import { getRecaptchaToken } from "../utils/recaptcha";

import { notifyUser } from "../utils/notifyUser";

function EntrepreneurApplication() {
    const { getConfig } = useConfig();

    const dispatch = useDispatch();

    const googleRecaptchaEnabled = getConfig("RECAPTCHA_ENABLED") === "true";

    // Load public configs when component mounts
    useEffect(() => {
        dispatch(loadPublicConfigs()).catch(() => {
            // Error loading configs
        });
    }, [dispatch]);

    useEffect(() => {
        if(googleRecaptchaEnabled) {
            const scriptId = "recaptcha-enterprise-script";

            if(!document.getElementById(scriptId)) {
                const script = document.createElement("script");
                const siteKey = getConfig("RECAPTCHA_SITE_KEY");

                script.id = scriptId;
                script.src = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}`;
                script.async = true;
                script.defer = true;
                document.body.appendChild(script);
            }

            return () => {
                const script = document.getElementById(scriptId);

                if(script) script.remove();
            };
        }
    }, [getConfig]);

    const navigate = useNavigate();
    const [activeSection] = useState("generalInfo");
    const [, setIsSubmitted] = useState(false);
    const [, setIsSubmitting] = useState(false);
    const [authMethod, setAuthMethod] = useState("credentials");
    const [pendingOAuthProfile, setPendingOAuthProfile] = useState(null);
    const [isCompletingOAuth, setIsCompletingOAuth] = useState(false);

    // Form Fields

    const [email, setEmail] = useState("");
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [signatureDataUrl, setSignatureDataUrl] = useState("");
    const [signatureName, setSignatureName] = useState("");
    const [signatureDate, setSignatureDate] = useState("");
    const [showSignatureModal, setShowSignatureModal] = useState(false);

    const sectionRef = useRef(null);

    // ...existing code...
    const googleEnabled =
        getConfig("GOOGLE_AUTH_ENABLED")?.toLowerCase() === "true";

    const microsoftEnabled =
        getConfig("MICROSOFT_AUTH_ENABLED")?.toLowerCase() === "true";

    // const [otpSent, setOtpSent] = useState(false);
    // const [otp, setOtp] = useState("");
    // const [otpVerified, setOtpVerified] = useState(false);
    // const [otpLoading, setOtpLoading] = useState(false);

    const [, setFormErrors] = useState({});

    // Add state for show/hide password toggles at the top of the component
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // Auto load cities when state changes

    // Save form into localStorage on change
    useEffect(() => {
        const formData = {
            email,
            username,
            password,
            confirmPassword,
        };

        localStorage.setItem("entrepreneurFormData", JSON.stringify(formData));
    }, [email, username, password, confirmPassword]);

    // Load form from localStorage on mount
    useEffect(() => {
        const savedData = JSON.parse(
            localStorage.getItem("entrepreneurFormData"),
        );

        if(savedData) {
            setEmail(savedData.email || "");
            setUsername(savedData.username || "");
            setPassword(savedData.password || "");
            setConfirmPassword(savedData.confirmPassword || "");
        }

        setSignatureDataUrl(
            localStorage.getItem("entrepreneurSignature") || "",
        );

        setSignatureName(
            localStorage.getItem("entrepreneurSignatureName") || "",
        );

        setSignatureDate(
            localStorage.getItem("entrepreneurSignatureDate") || "",
        );
    }, []);

    // Check if user is already authenticated (OAuth user)
    useEffect(() => {
        const jwtToken = localStorage.getItem("jwtToken");
        const userEmail = localStorage.getItem("userEmail");

        if(jwtToken && userEmail) {
            // User is already authenticated, likely from OAuth
            setAuthMethod("oauth");
            setEmail(userEmail);

            console.log("Detected OAuth user, switching to OAuth mode");
        }
    }, []);

    useEffect(() => {
        const loadPendingOAuthProfile = async() => {
            try {
                const { data } = await axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/api/auth/oauth/pending-registration`,
                    { withCredentials: true },
                );

                if(data.pending) {
                    setPendingOAuthProfile(data);
                    setEmail(data.email || "");
                    setUsername(data.email?.split("@")[0] || "");
                    setAuthMethod("oauth");
                }
            } catch(error) {
                console.error("Failed to load pending OAuth registration:", error);
            }
        };

        loadPendingOAuthProfile();
    }, []);

    // Email and phone validators
    const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    // Check for signature data on component mount
    useEffect(() => {
        const signatureText = localStorage.getItem("entrepreneurSignatureName");
        const signatureDate = localStorage.getItem("entrepreneurSignatureDate");

        if(!signatureText || !signatureDate) {
            notifyUser(null, "error", "Please check terms and conditions and provide your signature.", "toast", null, null, null, null, null, false, false, "top-center");

            setShowSignatureModal(true);
        }
    }, []);

    // --- VALIDATIONS ---

    // password validators (allow only configured characters and min length)
    const passwordMinLengthStr = getConfig("REACT_APP_PASSWORD_MIN_LENGTH");
    const passwordMinLength = passwordMinLengthStr
        ? parseInt(passwordMinLengthStr, 10)
        : 8;

    // Load password regex from config with safe fallback
    const passwordRegexCfg = getConfig("REACT_APP_PASSWORD_REGEX");
    let passwordAllowedRegex;
    try {
        passwordAllowedRegex = passwordRegexCfg
            ? new RegExp(passwordRegexCfg)
            : /^[a-zA-Z0-9#$&_\-+%|\[\]@;!()]+$/;
    } catch (e) {
        passwordAllowedRegex = /^[a-zA-Z0-9#$&_\-+%|\[\]@;!()]+$/;
    }

    const isStrongPassword = (password) => {
        if (!password) return false;
        if (password.length < passwordMinLength) return false;
        return passwordAllowedRegex.test(password);
    };

    const validateLogin = () => {
        const errors = {};

        if(authMethod === "oauth") {
            return errors;
        }

        // Skip password validation for OAuth users
        if(authMethod === "credentials") {
            if(!password.trim()) {
                errors.password = "Password is required";
                notifyUser(null, "error", errors.password, "toast", null, null, null, null, null, false, false, "top-center");
            } else if(username && password === username) {
                errors.password = "Password cannot be the same as username";

                notifyUser(null, "error", errors.password, "toast", null, null, null, null, null, false, false, "top-center");
            } else if(!isStrongPassword(password)) {
                errors.password = `Password must be at least ${passwordMinLength} characters and may only contain letters, numbers, and these special characters: # $ & _ - + % | [ ] @ ; ! ( )`;

                notifyUser(null, "error", errors.password, "toast", null, null, null, null, null, false, false, "top-center");
            }

            if(!confirmPassword.trim() || confirmPassword !== password) {
                errors.confirmPassword = "Passwords do not match";

                notifyUser(null, "error", errors.confirmPassword, "toast", null, null, null, null, null, false, false, "top-center");
            }
        }

        // For both OAuth and credentials, validate email format only if email is provided
        if(email && !isValidEmail(email)) {
            errors.email = "Invalid email format";

            notifyUser(null, "error", errors.email, "toast", null, null, null, null, null, false, false, "top-center");
        }

        return errors;
    };

    const completePendingOAuthRegistration = async() => {
        if(!pendingOAuthProfile) return;

        setIsCompletingOAuth(true);
        try {
            const { data } = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/auth/oauth/complete-registration`,
                { role: "entrepreneur" },
                { withCredentials: true },
            );

            window.location.href = `/oauth-complete?token=${encodeURIComponent(data.token)}&email=${encodeURIComponent(data.email)}`;
        } catch(error) {
            if(error.response?.status === 410) {
                setPendingOAuthProfile(null);
            }

            notifyUser(null, "error", error.response?.data?.error ||
                "Unable to complete registration. Please try again.", "toast", null, null, null, null, null, false, false, "top-center");
        } finally {
            setIsCompletingOAuth(false);
        }
    };

    const handleOAuthSignupEntrepreneur = (provider) => async () => {
        try {
            // Save role before OAuth
            localStorage.setItem("oauthRole", "entrepreneur");

            await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/auth/set-role`,
                {
                    role: "entrepreneur",
                },

                { withCredentials: true },
            );

            // Then redirect to Google/Microsoft login - include debug query param
            window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/${provider}?source=entrepreneurApplication&intent=signup`;
        } catch(err) {
            console.error("Error initiating OAuth", err);

            notifyUser(null, "error", "Failed to start OAuth flow. Please try again.", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        setIsSubmitting(true);

        console.log("handleSubmit called");

        if(authMethod === "oauth") {
            console.log(
                "Form submit attempted in OAuth mode. Silently ignoring.",
            );

            return;
        }
        try {
            const loginErrors = validateLogin();

            if(Object.keys(loginErrors).length > 0) {
                console.log("Validation errors:", loginErrors);

                setFormErrors(loginErrors);
                setIsSubmitting(false);

                return;
            }

            let siteKey = null;
            let recaptcha_token = null;
            let recaptcha_action = "";

            if(googleRecaptchaEnabled) {
                recaptcha_action = "registration";
                siteKey = getConfig("RECAPTCHA_SITE_KEY");
                recaptcha_token = await getRecaptchaToken(
                    recaptcha_action,
                    siteKey,
                );

                if(!recaptcha_token) {
                    notifyUser(null, "error", "Couldn’t get verification token. Check your network/ad-blocker and try again.", "toast", null, null, null, null, null, false, false, "top-center");

                    setIsSubmitting(false);

                    return;
                }
            }

            console.log("Sending user data...");

            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/entrepenuer`,
                {
                    email,
                    username,
                    password,
                    confirmPassword,
                    signatureDataUrl,
                    signatureName,
                    signatureDate,
                    recaptcha_action,
                    recaptcha_token,
                },
            );

            const userId = response.data.userId;

            console.log("User created, userId:", userId);

            console.log("Reached final success block");

            notifyUser(null, "success", <div>
                    <h4 className="text-navy font-semibold">Check Your Email to Continue</h4>
                    <div className="text-sm text-gray-700 mt-1">
                        We&apos;ve sent a verification link to the email address you provided. Please check your inbox (and spam folder, just in case) and click the link to continue your application.
                    </div>
                </div>, "toast", null, null, 8000, null, null, false, false, "top-center");

            setIsSubmitted(true);

            localStorage.removeItem("entrepreneurFormData");
            localStorage.removeItem("entrepreneurSignature");
            localStorage.removeItem("entrepreneurSignatureName");
            localStorage.removeItem("entrepreneurSignatureDate");

            setTimeout(() => {
                navigate("/login", {
                    state: {
                        verificationToastText:
                            "We've sent a verification link to the email address you provided. Please check your inbox (and spam folder, just in case) and click the link to continue your application.",
                    },
                });
            }, 8000);
        } catch(error) {
            console.error("Catch block error:", error);

            notifyUser(null, "error", error?.response?.data?.message, "toast", null, null, null, null, null, false, false, "top-center");
        } finally {
            setIsSubmitting(false);
        }
    };

    // Set page title
    useEffect(() => {
        document.title = "entrepreneurApplication | AQI";
    }, []);

    useEffect(() => {
        if(sectionRef.current) {
            sectionRef.current.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        }
    }, [activeSection]);

    return (
        <div className="font-poppins">
            <>
                <TopBar />
                <div className="flex flex-col min-h-screen bg-white">
                    <main className="flex justify-center flex-grow">
                        <div className="w-full max-w-6xl px-4 pb-4 bg-white shadow-xl rounded-xl-xl sm:px-6 md:px-8">
                            {/* Navigation Buttons */}

                            <div>
                                <h1 className="text-center text-4xl font-semibold text-navy mt-6 font-poppins">
                                    Entrepreneur Application
                                </h1>
                            </div>

                            <div className="w-full overflow-x-auto">
                                <div className="flex justify-between sm:gap-4 mb-6 min-w-[640px]"></div>
                            </div>

                            <form onSubmit={handleSubmit}>
                                <div>
                                    <h4 className="text-center text-lg text-navy mb-2 font-semibold font-poppins">
                                        We&apos;re excited to learn about your
                                        venture- show us what makes your
                                        business bold, brilliant, and uniquely
                                        you.
                                    </h4>
                                    <h4 className="text-center text-lg text-navy mb-2 font-semibold font-poppins">
                                        Please create an account to continue. Or{" "}
                                        <a
                                            href="/login"
                                            className="underline text-navy hover:text-[#002f47] transition"
                                        >
                                            Signin With
                                        </a>
                                    </h4>
                                    <div className="flex justify-center mt-2 mb-8">
                                        <div className="flex p-1 mt-4 bg-gray-100 rounded-full shadow-sm">
                                            <button
                                                onClick={() =>
                                                    setAuthMethod("credentials")
                                                }
                                                className={`px-5 py-2 text-sm font-semibold rounded-full transition-all duration-200 ${
                                                    authMethod === "credentials"
                                                        ? "bg-white text-navy shadow"
                                                        : "text-gray-500"
                                                }`}
                                            >
                                                Email & Password
                                            </button>
                                            {(googleEnabled ||
                                                microsoftEnabled) && (
                                                <button
                                                    onClick={() =>
                                                        setAuthMethod("oauth")
                                                    }
                                                    className={`px-5 py-2 text-sm font-semibold rounded-full transition-all duration-200 ${
                                                        authMethod === "oauth"
                                                            ? "bg-white text-navy shadow"
                                                            : "text-gray-500"
                                                    }`}
                                                >
                                                    Google / Microsoft
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex flex-col justify-center gap-8 md:flex-row">
                                        {authMethod === "credentials" && (
                                            <div className="w-full space-y-4 md:w-1/2">
                                                <div className="flex flex-col">
                                                    <label className="text-navy font-semibold font-poppins">
                                                        Email*
                                                    </label>
                                                    <input
                                                        type="text"
                                                        placeholder="Email"
                                                        value={email}
                                                        onChange={(e) => {
                                                            setEmail(
                                                                e.target.value,
                                                            );
                                                        }}
                                                        className="p-2 border border-navy rounded-xl font-poppins"
                                                        required
                                                    />
                                                </div>
                                                <div className="flex flex-col">
                                                    <label className="text-navy font-semibold font-poppins">
                                                        Password*
                                                    </label>
                                                    <div className="relative">
                                                        <input
                                                            type={
                                                                showPassword
                                                                    ? "text"
                                                                    : "password"
                                                            }
                                                            placeholder="Password"
                                                            value={password}
                                                            onChange={(e) =>
                                                                setPassword(
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                            className="p-2 border border-navy rounded-xl font-poppins w-full pr-10"
                                                            required
                                                        />
                                                        <span
                                                            className="absolute flex items-center justify-center w-6 h-6 p-0 m-0 text-gray-500 -translate-y-1/2 bg-transparent border-none cursor-pointer right-3 top-1/2 focus:outline-none"
                                                            onClick={() =>
                                                                setShowPassword(
                                                                    (prev) =>
                                                                        !prev,
                                                                )
                                                            }
                                                            tabIndex={0}
                                                            role="button"
                                                            aria-label={
                                                                showPassword
                                                                    ? "Hide password"
                                                                    : "Show password"
                                                            }
                                                        >
                                                            {showPassword ? (
                                                                <FaEyeSlash />
                                                            ) : (
                                                                <FaEye />
                                                            )}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="flex flex-col">
                                                    <label className="text-navy font-semibold font-poppins">
                                                        Confirm Password*
                                                    </label>
                                                    <div className="relative">
                                                        <input
                                                            type={
                                                                showConfirmPassword
                                                                    ? "text"
                                                                    : "password"
                                                            }
                                                            placeholder="Confirm Password"
                                                            value={
                                                                confirmPassword
                                                            }
                                                            onChange={(e) =>
                                                                setConfirmPassword(
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                            className="p-2 border border-navy rounded-xl font-poppins w-full pr-10"
                                                            required
                                                        />
                                                        <span
                                                            className="absolute flex items-center justify-center w-6 h-6 p-0 m-0 text-gray-500 -translate-y-1/2 bg-transparent border-none cursor-pointer right-3 top-1/2 focus:outline-none"
                                                            onClick={() =>
                                                                setShowConfirmPassword(
                                                                    (prev) =>
                                                                        !prev,
                                                                )
                                                            }
                                                            tabIndex={0}
                                                            role="button"
                                                            aria-label={
                                                                showConfirmPassword
                                                                    ? "Hide password"
                                                                    : "Show password"
                                                            }
                                                        >
                                                            {showConfirmPassword ? (
                                                                <FaEyeSlash />
                                                            ) : (
                                                                <FaEye />
                                                            )}
                                                        </span>
                                                    </div>
                                                </div>

                                                <div className="flex justify-end">
                                                    <button
                                                        type="submit"
                                                        className="aqi-primary-action w-full rounded-xl bg-navy py-3 font-bold text-white shadow-md hover:bg-navy-2 sm:w-1/2 font-poppins"
                                                    >
                                                        SUBMIT
                                                    </button>
                                                </div>
                                                <p className="text-center text-sm text-navy font-light font-poppins">
                                                    By creating this account you
                                                    agree to all terms and
                                                    conditions.
                                                </p>
                                            </div>
                                        )}

                                        {authMethod === "oauth" &&
                                            (pendingOAuthProfile || googleEnabled || microsoftEnabled) && (
                                                <div className="flex flex-col items-center w-full p-6 bg-white border border-gray-100 shadow md:w-96 rounded-xl">
                                                    <p className="text-center font-semibold text-navy font-poppins mb-4">
                                                        Continue with your
                                                        preferred account
                                                    </p>
                                                    <div className="flex flex-col w-full gap-4">
                                                        {pendingOAuthProfile && (
                                                            <>
                                                                <div className="space-y-3 rounded-lg bg-blue-50 p-4">
                                                                    <p className="text-sm font-semibold text-navy">
                                                                        {pendingOAuthProfile.provider === "google" ? "Google" : "Microsoft"} account connected
                                                                    </p>
                                                                    <input
                                                                        type="text"
                                                                        value={pendingOAuthProfile.firstName || ""}
                                                                        aria-label="First name"
                                                                        readOnly
                                                                        className="w-full rounded-lg border border-blue-200 bg-white p-2 text-sm"
                                                                    />
                                                                    <input
                                                                        type="text"
                                                                        value={pendingOAuthProfile.lastName || ""}
                                                                        aria-label="Last name"
                                                                        readOnly
                                                                        className="w-full rounded-lg border border-blue-200 bg-white p-2 text-sm"
                                                                    />
                                                                    <input
                                                                        type="email"
                                                                        value={email}
                                                                        aria-label="Email"
                                                                        readOnly
                                                                        className="w-full rounded-lg border border-blue-200 bg-white p-2 text-sm"
                                                                    />
                                                                </div>
                                                                <button
                                                                    type="button"
                                                                    onClick={completePendingOAuthRegistration}
                                                                    disabled={isCompletingOAuth}
                                                                    className="rounded-[6px] bg-navy p-3 font-semibold text-white disabled:opacity-60"
                                                                >
                                                                    {isCompletingOAuth ? "CREATING ACCOUNT..." : "CONTINUE AS ENTREPRENEUR"}
                                                                </button>
                                                            </>
                                                        )}
                                                        {!pendingOAuthProfile && googleEnabled && (
                                                            <button
                                                                onClick={handleOAuthSignupEntrepreneur(
                                                                    "google",
                                                                )}
                                                                className="flex items-center justify-center gap-3 rounded-[6px] border border-navy p-2 transition hover:bg-gray-50 font-poppins"
                                                            >
                                                                <img
                                                                    src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                                                                    alt="Google Logo"
                                                                    className="w-5 h-5"
                                                                />
                                                                <span className="text-sm font-medium text-navy font-poppins">
                                                                    Continue
                                                                    with Google
                                                                </span>
                                                            </button>
                                                        )}
                                                        {!pendingOAuthProfile && microsoftEnabled && (
                                                            <button
                                                                onClick={handleOAuthSignupEntrepreneur(
                                                                    "microsoft",
                                                                )}
                                                                className="flex items-center justify-center gap-3 rounded-[6px] border border-navy p-2 transition hover:bg-gray-50 font-poppins"
                                                            >
                                                                <img
                                                                    src="https://upload.wikimedia.org/wikipedia/commons/4/44/Microsoft_logo.svg"
                                                                    alt="Microsoft Logo"
                                                                    className="w-5 h-5 ml-4 "
                                                                />
                                                                <span className="text-sm font-medium text-navy font-poppins">
                                                                    Continue
                                                                    with
                                                                    Microsoft
                                                                </span>
                                                            </button>
                                                        )}
                                                    </div>
                                                    <p className="mt-6 text-xs text-center text-gray-500 font-poppins">
                                                        We’ll use your verified
                                                        email from these
                                                        services.
                                                    </p>
                                                </div>
                                            )}
                                    </div>
                                </div>
                            </form>
                        </div>
                    </main>

                    <Footer />
                </div>
            </>

            {/* Signature Modal */}
            {showSignatureModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black bg-opacity-50">
                    <div className="w-full max-w-4xl max-h-[85vh] overflow-y-auto bg-white rounded-lg shadow-xl p-6 text-center border border-navy relative">
                        <button
                            className="absolute top-4 right-4 text-navy text-2xl font-bold hover:text-[#D4AF37] focus:outline-none"
                            onClick={() => setShowSignatureModal(false)}
                            aria-label="Close"
                        >
                            &times;
                        </button>
                        <EntrepreneurTermsNConditions
                            onBack={() => setShowSignatureModal(false)}
                            onClose={() => setShowSignatureModal(false)}
                            onSuccess={() => {
                                setShowSignatureModal(false);
                                // Reload signature data
                                setSignatureDataUrl(
                                    localStorage.getItem(
                                        "entrepreneurSignature",
                                    ) || "",
                                );
                                setSignatureName(
                                    localStorage.getItem(
                                        "entrepreneurSignatureName",
                                    ) || "",
                                );
                                setSignatureDate(
                                    localStorage.getItem(
                                        "entrepreneurSignatureDate",
                                    ) || "",
                                );
                            }}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}

export default EntrepreneurApplication;
